<template>
  <app-content :title="'业务转凭证-历史记录'">
    <div class="history-page">
      <!-- 搜索栏 -->
      <div class="search-bar">
        <div class="search-filters">
          <div class="filter-item">
            <label>平台：</label>
            <select v-model="searchForm.platform" @change="handleSearch">
              <option value="">全部</option>
              <option value="amazon">亚马逊</option>
              <option value="shopify">Shopify</option>
              <option value="offline">线下</option>
            </select>
          </div>

          <div class="filter-item">
            <label>状态：</label>
            <select v-model="searchForm.status" @change="handleSearch">
              <option value="">全部</option>
              <option value="PARSED">已解析</option>
              <option value="GENERATED">已生成</option>
            </select>
          </div>

          <div class="filter-item">
            <label>业务日期：</label>
            <input
              v-model="searchForm.startDate"
              type="date"
              @change="handleSearch"
            />
            <span style="margin: 0 8px">至</span>
            <input
              v-model="searchForm.endDate"
              type="date"
              @change="handleSearch"
            />
          </div>

          <button class="btn btn-primary" @click="handleSearch">
            <i class="h-icon-search"></i>
            搜索
          </button>
        </div>
      </div>

      <!-- 任务列表 -->
      <div class="task-list">
        <div v-if="loading" class="loading-container">
          <i class="h-icon-loading"></i>
          加载中...
        </div>

        <div v-else-if="taskList.length === 0" class="empty-container">
          <i class="h-icon-inbox"></i>
          <p>暂无历史记录</p>
        </div>

        <div v-else class="task-items">
          <div
            v-for="task in taskList"
            :key="task.taskId"
            class="task-card"
            :class="{ expanded: task.expanded }"
          >
            <!-- 任务头部 -->
            <div class="task-header" @click="toggleExpand(task)">
              <div class="task-info">
                <div class="task-title">
                  <span class="task-no">{{ task.taskNo }}</span>
                  <span class="status-tag" :class="'status-' + task.status.toLowerCase()">
                    {{ getStatusText(task.status) }}
                  </span>
                  <span class="platform-tag">{{ task.platform }}</span>
                </div>
                <div class="task-meta">
                  <span class="meta-item">
                    <i class="h-icon-calendar"></i>
                    {{ formatDate(task.businessDate) }}
                  </span>
                  <span class="meta-item">
                    <i class="h-icon-file"></i>
                    {{ task.fileName }}
                  </span>
                  <span class="meta-item">
                    <i class="h-icon-list"></i>
                    {{ task.detailCount }} 条业务
                  </span>
                  <span class="meta-item">
                    <i class="h-icon-money"></i>
                    ¥{{ formatMoney(task.totalAmount) }}
                  </span>
                </div>
              </div>

              <div class="task-actions" @click.stop>
                <button
                  v-if="task.status === 'PARSED'"
                  class="btn-icon btn-primary"
                  @click="handleGenerate(task)"
                  title="生成凭证"
                >
                  <i class="h-icon-check"></i>
                </button>
                <button
                  v-if="task.status === 'GENERATED'"
                  class="btn-icon btn-success"
                  @click="handleRegenerate(task)"
                  title="重新生成"
                >
                  <i class="h-icon-refresh"></i>
                </button>
                <button
                  class="btn-icon btn-danger"
                  @click="handleDelete(task)"
                  title="删除"
                >
                  <i class="h-icon-trash"></i>
                </button>
                <i
                  class="h-icon-down expand-icon"
                  :class="{ expanded: task.expanded }"
                ></i>
              </div>
            </div>

            <!-- 任务详情（展开时显示） -->
            <div v-if="task.expanded" class="task-details">
              <div class="detail-section">
                <h4>业务明细（{{ task.details ? task.details.length : 0 }}条）</h4>
                <div v-if="loadingDetails" class="loading-text">
                  <i class="h-icon-loading"></i> 加载中...
                </div>
                <table v-else-if="task.details && task.details.length > 0" class="detail-table">
                  <thead>
                    <tr>
                      <th width="20%">场景名称</th>
                      <th width="15%">金额</th>
                      <th width="25%">借方科目</th>
                      <th width="25%">贷方科目</th>
                      <th width="15%">摘要</th>
                      <th width="12%">Decision Memory</th>
                      <th width="18%">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="detail in task.details" :key="detail.id">
                      <td>{{ detail.scenarioName }}</td>
                      <td class="text-right amount-cell">
                        {{ formatMoney(detail.amount) }}
                      </td>
                      <td>
                        <span class="subject-code">{{ detail.debitSubjectCode }}</span>
                        <span class="subject-name">{{ detail.debitSubjectName }}</span>
                      </td>
                      <td>
                        <span class="subject-code">{{ detail.creditSubjectCode }}</span>
                        <span class="subject-name">{{ detail.creditSubjectName }}</span>
                      </td>
                      <td class="summary-cell">{{ detail.summary }}</td>
                      <td>
                        <template v-if="extractMemoryMeta(detail).reuseMode">
                          <span class="memory-tag" :class="'memory-' + String(extractMemoryMeta(detail).reuseMode).toLowerCase()">
                            {{ extractMemoryMeta(detail).reuseMode }}
                          </span>
                          <div v-if="extractMemoryMeta(detail).candidateStatus" class="memory-subtag">
                            Candidate: {{ extractMemoryMeta(detail).candidateStatus }}
                          </div>
                        </template>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td class="detail-actions">
                        <button class="btn-link" @click="openBusinessPage(task, detail, 'review')">查看审核</button>
                        <button class="btn-link" @click="openBusinessPage(task, detail, 'memory')">查看 Decision Memory</button>
                        <button class="btn-link" @click="openBusinessPage(task, detail, 'draft')">查看凭证草稿</button>
                        <button v-if="extractMemoryMeta(detail).traceId" class="btn-link" @click="openBusinessPage(task, detail, 'evidence')">查看证据链</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-if="task.status === 'GENERATED'" class="detail-section">
                <h4>
                  生成的凭证（{{ task.voucherCount }}张）
                  <button class="btn-link" @click="goToVoucherList">查看凭证列表 →</button>
                </h4>
                <div class="voucher-ids">
                  {{ task.voucherIds || '无' }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <div v-if="total > pageSize" class="pagination">
        <button
          class="page-btn"
          :disabled="currentPage === 1"
          @click="handlePageChange(currentPage - 1)"
        >
          上一页
        </button>
        <span class="page-info">
          第 {{ currentPage }} / {{ Math.ceil(total / pageSize) }} 页，共 {{ total }} 条
        </span>
        <button
          class="page-btn"
          :disabled="currentPage >= Math.ceil(total / pageSize)"
          @click="handlePageChange(currentPage + 1)"
        >
          下一页
        </button>
      </div>
    </div>
  </app-content>
</template>

<script>
export default {
  name: 'BusinessVoucherHistory',
  data() {
    return {
      loading: false,
      loadingDetails: false,
      searchForm: {
        platform: '',
        status: '',
        startDate: '',
        endDate: ''
      },
      taskList: [],
      currentPage: 1,
      pageSize: 10,
      total: 0
    };
  },
  mounted() {
    this.loadTasks();
  },
  methods: {
    // 加载任务列表
    async loadTasks() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          limit: this.pageSize,
          ...this.searchForm
        };

        const response = await this.$api.businessVoucher.listTasks(params);
        if (response && response.code === 200) {
          // 适配MyBatis-Plus的分页结构
          const pageData = response.data;
          this.taskList = (pageData.records || pageData.list || []).map(task => ({
            ...task,
            taskId: task.id, // 映射 id 为 taskId
            expanded: false,
            details: null
          }));
          this.total = pageData.total || 0;
        } else {
          this.$Message.error(response.message || response.msg || '加载失败');
        }
      } catch (error) {
        console.error('加载任务列表失败', error);
        this.$Message.error('加载失败');
      } finally {
        this.loading = false;
      }
    },

    // 搜索
    handleSearch() {
      this.currentPage = 1;
      this.loadTasks();
    },

    // 翻页
    handlePageChange(page) {
      this.currentPage = page;
      this.loadTasks();
    },

    // 展开/折叠
    async toggleExpand(task) {
      task.expanded = !task.expanded;

      // 如果展开且还没有加载详情，则加载
      if (task.expanded && !task.details) {
        await this.loadTaskDetails(task);
      }

      this.$forceUpdate();
    },

    // 加载任务详情
    async loadTaskDetails(task) {
      this.loadingDetails = true;
      try {
        const response = await this.$api.businessVoucher.getTaskDetail(task.taskId);
        if (response && response.code === 200) {
          task.details = response.data.details;
        }
      } catch (error) {
        console.error('加载详情失败', error);
      } finally {
        this.loadingDetails = false;
        this.$forceUpdate();
      }
    },

    // 生成凭证
    async handleGenerate(task) {
      try {
        await this.$Confirm(`确定要为任务"${task.taskNo}"生成凭证吗？`, '生成凭证');
        
        const response = await this.$api.businessVoucher.generateVouchers(task.taskId);
        if (response && response.code === 200) {
          this.$Message.success('凭证生成成功！');
          this.loadTasks();
        } else {
          this.$Message.error(response.msg || '生成失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('生成凭证失败', error);
          this.$Message.error(error.message || '生成失败');
        }
      }
    },

    // 重新生成
    async handleRegenerate(task) {
      try {
        await this.$Confirm(
          `确定要重新生成凭证吗？这将删除之前生成的 ${task.voucherCount} 张凭证。`,
          '重新生成凭证'
        );
        
        const response = await this.$api.businessVoucher.generateVouchers(task.taskId);
        if (response && response.code === 200) {
          this.$Message.success('凭证重新生成成功！');
          this.loadTasks();
        } else {
          this.$Message.error(response.msg || '生成失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('重新生成失败', error);
          this.$Message.error(error.message || '重新生成失败');
        }
      }
    },

    // 删除任务
    async handleDelete(task) {
      try {
        const message = task.status === 'GENERATED'
          ? `确定要删除任务"${task.taskNo}"吗？注意：请确认已经手动删除全部相关凭证！`
          : `确定要删除任务"${task.taskNo}"吗？`;
        
        await this.$Confirm(message, '删除任务');
        
        const response = await this.$api.businessVoucher.deleteTask(task.taskId);
        if (response && response.code === 200) {
          this.$Message.success('删除成功');
          this.loadTasks();
        } else {
          this.$Message.error(response.msg || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败', error);
          this.$Message.error('删除失败');
        }
      }
    },

    // 跳转到凭证列表
    goToVoucherList() {
      this.$router.push('/voucher');
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        UPLOADED: '已上传',
        PARSED: '已解析',
        PREVIEWED: '已预览',
        GENERATED: '已生成',
        FAILED: '失败'
      };
      return statusMap[status] || status;
    },

    // 格式化日期
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    // 格式化金额
    formatMoney(amount) {
      if (!amount) return '0.00';
      return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    extractMemoryMeta(detail) {
      if (!detail || !detail.extData) {
        return {};
      }
      try {
        const extData = typeof detail.extData === 'string' ? JSON.parse(detail.extData) : detail.extData;
        const accountingDecision = extData.accountingDecision || {};
        const memoryMatch = accountingDecision.decisionMemoryMatch || {};
        const candidate = extData.memoryCandidate || {};
        return {
          reuseMode: memoryMatch.reuseMode || '',
          traceId: extData.traceId || '',
          candidateStatus: candidate.status || ''
        };
      } catch (e) {
        return {};
      }
    },

    openBusinessPage(task, detail, mode) {
      const meta = this.extractMemoryMeta(detail);
      const query = {
        taskId: task.taskId || task.id,
        focusDetailId: detail.id
      };
      if (mode === 'evidence' && meta.traceId) {
        query.evidenceTraceId = meta.traceId;
      }
      this.$router.push({
        path: '/voucher/business',
        query
      });
    }
  }
};
</script>

<style scoped lang="less">
.history-page {
  padding: 20px;
}

.search-bar {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.search-filters {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 8px;

  label {
    font-weight: 500;
    color: #606266;
  }

  select,
  input {
    height: 36px;
    padding: 0 12px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: #409eff;
    }
  }

  select {
    min-width: 120px;
  }

  input[type='date'] {
    width: 150px;
  }
}

.task-list {
  background: #fff;
  border-radius: 4px;
  min-height: 400px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.loading-container,
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #909399;

  i {
    font-size: 48px;
    margin-bottom: 16px;

    &.h-icon-loading {
      animation: rotate 1s linear infinite;
    }
  }

  p {
    font-size: 16px;
  }
}

.task-items {
  padding: 16px;
}

.task-card {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  margin-bottom: 12px;
  background: #fafafa;
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  }

  &.expanded {
    background: #fff;
  }
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  cursor: pointer;
  user-select: none;
}

.task-info {
  flex: 1;
}

.task-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;

  .task-no {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }

  .status-tag {
    padding: 2px 8px;
    border-radius: 3px;
    font-size: 12px;
    font-weight: 500;

    &.status-parsed {
      background: #e1f3d8;
      color: #67c23a;
    }

    &.status-generated {
      background: #d9ecff;
      color: #409eff;
    }

    &.status-failed {
      background: #fef0f0;
      color: #f56c6c;
    }
  }

  .platform-tag {
    padding: 2px 8px;
    background: #f0f0f0;
    border-radius: 3px;
    font-size: 12px;
    color: #909399;
  }
}

.task-meta {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;

  .meta-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    color: #606266;

    i {
      font-size: 14px;
      color: #909399;
    }
  }
}

.task-actions {
  display: flex;
  align-items: center;
  gap: 8px;

  .expand-icon {
    font-size: 16px;
    color: #909399;
    transition: transform 0.3s;
    margin-left: 8px;

    &.expanded {
      transform: rotate(180deg);
    }
  }
}

.btn-icon {
  width: 32px;
  height: 32px;
  border: 1px solid #dcdfe6;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;

  i {
    font-size: 16px;
  }

  &.btn-primary {
    border-color: #409eff;
    color: #409eff;

    &:hover {
      background: #409eff;
      color: #fff;
    }
  }

  &.btn-success {
    border-color: #67c23a;
    color: #67c23a;

    &:hover {
      background: #67c23a;
      color: #fff;
    }
  }

  &.btn-info {
    border-color: #909399;
    color: #909399;

    &:hover {
      background: #909399;
      color: #fff;
    }
  }

  &.btn-danger {
    border-color: #f56c6c;
    color: #f56c6c;

    &:hover {
      background: #f56c6c;
      color: #fff;
    }
  }
}

.task-details {
  border-top: 1px solid #e4e7ed;
  padding: 16px;
  background: #fff;
}

.detail-section {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }

  h4 {
    font-size: 15px;
    color: #303133;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}

.loading-text {
  text-align: center;
  padding: 20px;
  color: #909399;

  i {
    animation: rotate 1s linear infinite;
    margin-right: 8px;
  }
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  thead {
    background: #f5f7fa;

    th {
      padding: 10px 12px;
      text-align: left;
      font-weight: 600;
      color: #303133;
      border: 1px solid #ebeef5;
    }
  }

  tbody {
    tr:hover {
      background: #fafafa;
    }

    td {
      padding: 10px 12px;
      border: 1px solid #ebeef5;
      color: #606266;

      &.text-right {
        text-align: right;
      }

      &.amount-cell {
        font-family: monospace;
        font-weight: 500;
      }

      &.summary-cell {
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  .subject-code {
    font-family: monospace;
    font-weight: 600;
    color: #409eff;
    margin-right: 6px;
  }

  .subject-name {
    color: #606266;
  }
}

.detail-actions {
  white-space: nowrap;
}

.memory-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: #f0f5ff;
  color: #1d39c4;
}

.memory-subtag {
  margin-top: 4px;
  font-size: 12px;
  color: #666;
}

.memory-direct_to_draft {
  background: #f6ffed;
  color: #389e0d;
}

.memory-hint_only {
  background: #fffbe6;
  color: #d48806;
}

.memory-full_analysis {
  background: #e6f7ff;
  color: #096dd9;
}

.text-muted {
  color: #999;
}

.btn-link {
  padding: 0;
  border: none;
  background: none;
  color: #409eff;
  cursor: pointer;
  font-size: 13px;

  &:hover {
    text-decoration: underline;
  }
}

.voucher-ids {
  padding: 12px;
  background: #f5f7fa;
  border-radius: 4px;
  font-family: monospace;
  font-size: 13px;
  color: #606266;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  padding: 16px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .page-btn {
    padding: 8px 16px;
    border: 1px solid #dcdfe6;
    background: #fff;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;

    &:hover:not(:disabled) {
      border-color: #409eff;
      color: #409eff;
    }

    &:disabled {
      background: #f5f7fa;
      color: #c0c4cc;
      cursor: not-allowed;
    }
  }

  .page-info {
    color: #606266;
    font-size: 14px;
  }
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &.btn-primary {
    background: #409eff;
    color: #fff;

    &:hover {
      background: #66b1ff;
    }
  }

  i {
    font-size: 14px;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
